import React, { useMemo, useState } from 'react';
import { ContextMenuContainer, ContextMenuProps } from './ContextMenuContainer';
import { ContextMenuItem } from './ContextMenuItem';
import { useReactFlow } from '@xyflow/react';
import { getDescriptionVersionIdByValueDate, InputNodeAction } from './utils';
import translations from './InputDataContextMenu.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { RelationNodeType } from './RelationNode';
import { path } from '../../paths';
import {
  DataObjectRelationNodeType,
  Frequency,
  getDayjs,
  getLatestOutputVersionId,
  valueDate as getValueDate,
  InputDataActiveInfoParams,
  ISO_DATE_FORMAT,
  ISODate,
} from '@atlas-frontend/sdk';
import { Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { InputDataActiveInfoModal } from '../input-data/InputDataActiveInfoModal';
import UriTemplate from 'uri-template-lite';
import {
  RelationsTablesModal,
  RelationsTablesModalParams,
} from './RelationsTablesModal';

const { addTranslations, useTranslation } = i18nNsFactory(
  'relation-input-context-menu',
);
addTranslations(translations);

type InputDataContextMenuProps = ContextMenuProps & {
  nodeId: string;
  onClose?: () => void;
};

export const InputDataContextMenu: React.FC<InputDataContextMenuProps> = ({
  nodeId,
  top,
  left,
  onClose,
}) => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { getNode } = useReactFlow();
  const [activeInputInfoParams, setActiveInputInfoParams] = useState<
    InputDataActiveInfoParams | undefined
  >(undefined);
  const [relationsTablesParams, setRelationsTablesParams] = useState<
    RelationsTablesModalParams | undefined
  >(undefined);

  const node = getNode(nodeId) as unknown as RelationNodeType;

  const handleNodeAction = (action: InputNodeAction) => {
    if (!node) return;

    const {
      descriptionId,
      groupId,
      activePeriods = [],
      frequency,
      childNodes,
      dataObjectVersions,
      nodeType,
      dataObjectType,
      isRootNode,
    } = node?.data ?? {};

    const nodeIsInAGroup =
      node?.data.dataObjectGroupCode != null &&
      node?.data.dataObjectGroupCode !== node?.id;

    const groupOrDescriptionId =
      (nodeType !== DataObjectRelationNodeType.GROUP
        ? `${groupId}-${descriptionId}`
        : groupId) || undefined;

    const allDataObjectVersions = [
      ...(dataObjectVersions ?? []),
      ...(childNodes ?? []).flatMap(
        (childNode) => childNode?.dataObjectVersions ?? [],
      ),
    ];

    const validRanges = activePeriods?.map(
      (period) =>
        [
          getDayjs(period.validFrom) || null,
          getDayjs(period.validTo) || null,
        ] as [Dayjs | null, Dayjs | null],
    );

    const valueDateFromGraphFilter = searchParams.get('valueDate');

    const latestValueDate = (
      !isRootNode
        ? (getValueDate(
            getDayjs(node.data?.latestValueDate),
            (frequency?.code as Frequency) || 'YEAR',
            validRanges,
          )?.format(ISO_DATE_FORMAT) ?? valueDateFromGraphFilter)
        : valueDateFromGraphFilter
    ) as ISODate;

    const latestVersionWithLatestValueDate = getLatestOutputVersionId(
      allDataObjectVersions,
      latestValueDate || undefined,
    );
    const descriptionVersionId = getDescriptionVersionIdByValueDate(
      valueDateFromGraphFilter,
      activePeriods,
    );

    switch (action) {
      case InputNodeAction.OpenInputData: {
        const url = new UriTemplate(
          `${path.inputDataView}{?date,type,groupMemberId}`.replace(
            ':id',
            '{id}',
          ),
        ).expand({
          id: nodeIsInAGroup ? groupId : descriptionId || '',
          date: valueDateFromGraphFilter,
          type: nodeIsInAGroup ? 'group' : 'description',
          groupMemberId: nodeIsInAGroup ? descriptionId : undefined,
        });
        window.open(url, '_blank');
        onClose?.();
        break;
      }
      case InputNodeAction.OpenInputDescription: {
        const url = new UriTemplate(
          path.inputDataObjectView.replace(':id', '{id}'),
        ).expand({ id: descriptionVersionId || '' });
        window.open(url, '_blank');
        onClose?.();
        break;
      }
      case InputNodeAction.OpenRelationsGraph: {
        const url = new UriTemplate(
          `${path.relations}{?type,groupOrDescriptionId,valueDate,version,showOneStep}`,
        ).expand({
          type: 'INPUT',
          groupOrDescriptionId,
          valueDate: latestValueDate,
          version:
            nodeType === DataObjectRelationNodeType.GROUP
              ? 'latest'
              : latestVersionWithLatestValueDate || undefined,
          showOneStep: true,
        });
        window.open(url, '_blank');
        onClose?.();
        break;
      }
      case InputNodeAction.OpenRelationsTable:
        if (groupOrDescriptionId && latestValueDate) {
          setRelationsTablesParams({
            groupOrDescriptionId,
            valueDate: latestValueDate,
            version:
              nodeType === DataObjectRelationNodeType.GROUP
                ? 'latest'
                : latestVersionWithLatestValueDate || undefined,
            dataObjectType,
            isGroup: nodeType === DataObjectRelationNodeType.GROUP,
            rootNodeName: node.data.label,
          });
        }
        break;
      default:
        if (groupId && latestValueDate) {
          setActiveInputInfoParams({
            groupId,
            valueDate: latestValueDate,
          });
        }
    }
  };

  const actions = useMemo<(InputNodeAction | 'divider')[]>(() => {
    const { isRootNode, nodeType } = node?.data ?? {};
    const isGroupNode = nodeType === DataObjectRelationNodeType.GROUP;

    const dependentActions = [];
    if (isRootNode && !isGroupNode) {
      dependentActions.push(InputNodeAction.OpenInputData);
      dependentActions.push(InputNodeAction.OpenInputDescription);
      dependentActions.push('divider');
    }
    if (!isRootNode) dependentActions.push(InputNodeAction.OpenRelationsGraph);

    return [
      ...dependentActions,
      InputNodeAction.OpenRelationsTable,
      InputNodeAction.OpenRelatedInputs,
    ] as (InputNodeAction | 'divider')[];
  }, [node]);

  return (
    <ContextMenuContainer top={top} left={left}>
      {actions.map((action, idx) =>
        action === 'divider' ? (
          <hr key={`divider-${idx}`} />
        ) : (
          <ContextMenuItem
            key={action}
            id={action}
            onClick={() => handleNodeAction(action)}
          >
            {t(`action.${action}`)}
          </ContextMenuItem>
        ),
      )}
      {activeInputInfoParams && (
        <InputDataActiveInfoModal
          params={activeInputInfoParams}
          onClose={() => {
            onClose?.();
            setActiveInputInfoParams(undefined);
          }}
        />
      )}
      {relationsTablesParams && (
        <RelationsTablesModal
          params={relationsTablesParams}
          onClose={() => {
            onClose?.();
            setRelationsTablesParams(undefined);
          }}
        />
      )}
    </ContextMenuContainer>
  );
};
